import React, {useState, useRef, useCallback, useEffect, Fragment} from 'react'
import {useTranslation} from 'react-i18next'
import {makeStyles} from '@material-ui/core'
import {Close} from '@material-ui/icons'
import {DndContext, useDroppable} from '@dnd-kit/core'
import File from 'assets/svgs/File.svg'
import UploadFile from 'assets/svgs/UploadFile.svg'
import {formatBytes} from 'helpers/fileHelper'
import edfImportType from './style'

const useStyles = makeStyles(edfImportType)
const MIN_FILE_SIZE = 1024

interface FileDropzoneProps {
  maxFileSizeMB?: number
  maxFileCount?: number
  acceptedFileExt?: string[]
  onChangeFiles?: (fileList: File[]) => void
}

const drawUploadedFiles = (
  files: File[],
  classes: ReturnType<typeof useStyles>,
  clearFile: (index: number) => void,
) => {
  if (files.length === 0)
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <img
          src={UploadFile}
          alt='upload file icon'
          style={{width: 48, height: 48}}
        />
        <div>Drag and drop files here, or click to upload</div>
      </div>
    )

  return files.map((file, idx) => (
    <div key={idx} className={classes.fileContainer}>
      <img src={File} alt='file icon' style={{width: 24, height: 24}} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className={classes.fileName}>{file.name}</div>(
        {formatBytes(file.size)})
      </div>
      <Close
        onClick={() => clearFile(idx)}
        style={{
          cursor: 'pointer',
        }}
      />
    </div>
  ))
}

export default function FileDropzone({
  maxFileSizeMB = 100,
  maxFileCount = 1,
  acceptedFileExt,
  onChangeFiles,
}: FileDropzoneProps) {
  const [files, setFiles] = useState<File[]>([])
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [hasFileError, setHasFileError] = useState({
    isError: false,
    message: '',
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const classes = useStyles()
  const {t} = useTranslation()

  // dnd-kit droppable (for UI integration with dnd-kit ecosystem)
  const {setNodeRef, isOver} = useDroppable({id: 'file-dropzone'})

  const hasFiles = files.length > 0

  const handleFileChange = useCallback(
    (files: FileList) => {
      const fileArray = Array.from(files)

      if (fileArray.length > maxFileCount) {
        setHasFileError({
          isError: true,
          message: t('IFileUploadCountLimitErrorMessage', {
            count: maxFileCount,
          }),
        })
        return
      }

      if (fileArray.some((file) => file.size > maxFileSizeMB * 1024 * 1024)) {
        setHasFileError({
          isError: true,
          message: t('IFileSizeExceedErrorMessage', {size: maxFileSizeMB}),
        })
        return
      }

      if (acceptedFileExt && acceptedFileExt.length > 0) {
        const invalidFile = fileArray.find(
          (file) =>
            !acceptedFileExt.some((ext) =>
              file.name.toLowerCase().endsWith(ext.toLowerCase()),
            ),
        )
        if (invalidFile) {
          setHasFileError({
            isError: true,
            message: t('IFileExtensionErrorMessage', {
              ext: acceptedFileExt.join(', '),
            }),
          })
          return
        }
      }

      setHasFileError({isError: false, message: ''})
      setFiles(fileArray)
      onChangeFiles?.(fileArray)
    },
    [onChangeFiles],
  )

  const handleNativeDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDraggingOver(false)
      handleFileChange(e.dataTransfer.files)
    },
    [handleFileChange],
  )

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDraggingOver(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node))
      setIsDraggingOver(false)
  }

  const handleClick = () => {
    if (!hasFiles) {
      inputRef.current?.click()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      inputRef.current?.click()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length)
      handleFileChange(e.target.files)
    e.target.value = ''
  }

  const clearFile = (index: number) => {
    const newFiles = files.filter((_, idx) => idx !== index)
    setFiles(newFiles)
  }

  const active = isDraggingOver || isOver

  return (
    <>
      <DndContext>
        <div
          ref={setNodeRef}
          className={`${classes.container} ${
            active ? classes.activeContainer : ''
          } ${hasFiles ? classes.containerHasFiles : ''}`}
          role='button'
          tabIndex={0}
          onDragOver={handleDragOver}
          onClick={handleClick}
          onDragLeave={handleDragLeave}
          onDrop={handleNativeDrop}
          onKeyDown={handleKeyDown}
        >
          {drawUploadedFiles(files, classes, clearFile)}
          <input
            ref={inputRef}
            type='file'
            accept={acceptedFileExt?.join(',')}
            style={{display: 'none'}}
            onChange={handleInputChange}
          />
        </div>
      </DndContext>
      {hasFileError.isError && (
        <div className={classes.errorText}>* {hasFileError.message}</div>
      )}
    </>
  )
}
